// src/admin/PrintCards.js - Thêm bộ lọc ngày đăng ký & công tắc ẩn/hiện học sinh Nghỉ luôn
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import Card from './Card';
import html2canvas from 'html2canvas';
import JSZip from 'jszip';
import { firestore } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import './PrintCards.css';

// Helper trích xuất Date an toàn từ các định dạng ngày khác nhau trong Firestore
const parseStudentDate = (dateVal) => {
  if (!dateVal) return null;
  if (dateVal.toDate && typeof dateVal.toDate === 'function') {
    return dateVal.toDate();
  }
  if (dateVal.seconds) {
    return new Date(dateVal.seconds * 1000);
  }
  if (typeof dateVal === 'string') {
    // Định dạng dd/mm/yyyy
    if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(dateVal)) {
      const parts = dateVal.split('/');
      return new Date(parseInt(parts[2], 10), parseInt(parts[1], 10) - 1, parseInt(parts[0], 10));
    }
    const d = new Date(dateVal);
    if (!isNaN(d.getTime())) return d;
  }
  if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
    return dateVal;
  }
  return null;
};

const PrintCards = () => {
  const { selectedClassForOutlet } = useOutletContext();

  const [studentsList, setStudentsList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Bộ lọc nâng cao
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showInactiveStudents, setShowInactiveStudents] = useState(false); // Mặc định ẩn thiếu nhi đã nghỉ luôn

  const [selectedCards, setSelectedCards] = useState([]);
  const [backgroundModalOpen, setBackgroundModalOpen] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState('bg-1');
  const [customBackground, setCustomBackground] = useState(null);

  const [showProgress, setShowProgress] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentCount, setCurrentCount] = useState(0);

  const cardRefs = useRef({});

  useEffect(() => {
    const fetchCurrentYearStudents = async () => {
      setLoading(true);
      try {
        const yearQuery = query(collection(firestore, 'academic_years'), where('isCurrent', '==', true));
        let yearSnap = await getDocs(yearQuery);
        let currentYearDoc = yearSnap.docs[0];

        if (!currentYearDoc) {
          const allYearsSnap = await getDocs(query(collection(firestore, 'academic_years'), orderBy('startDate', 'desc')));
          if (!allYearsSnap.empty) {
            currentYearDoc = allYearsSnap.docs[0];
          }
        }

        if (!currentYearDoc) {
          setStudentsList([]);
          setLoading(false);
          return;
        }

        const currentYearId = currentYearDoc.id;

        const enrollmentsSnap = await getDocs(collection(firestore, `academic_years/${currentYearId}/enrollments`));
        const enrollmentsMap = {};
        enrollmentsSnap.docs.forEach(d => {
          const data = d.data();
          enrollmentsMap[data.studentId || d.id] = data;
        });

        const studentsSnap = await getDocs(collection(firestore, 'students'));
        
        const combined = [];
        studentsSnap.docs.forEach(docSnap => {
          const sId = docSnap.id;
          const profile = docSnap.data();
          const enrollment = enrollmentsMap[sId];

          if (enrollment) {
            combined.push({
              ...profile,
              id: sId,
              classId: enrollment.classId,
              gradeId: enrollment.gradeId,
              status: profile.status || enrollment.status || 'Còn đi học',
              createdAt: profile.createdAt || enrollment.createdAt || profile.registeredAt || profile.registrationDate || profile.updatedAt || null
            });
          } else if (profile.status === 'Nghỉ luôn' || profile.status === 'Nghỉ học') {
            combined.push({
              ...profile,
              id: sId,
              classId: profile.classId || profile.classNameId || 'CHUA_XEP_LOP',
              gradeId: profile.gradeId || '',
              status: profile.status,
              createdAt: profile.createdAt || profile.registeredAt || profile.registrationDate || profile.updatedAt || null
            });
          }
        });

        setStudentsList(combined);

      } catch (err) {
        console.error("Lỗi khi tải danh sách thiếu nhi từ Firestore:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentYearStudents();
  }, []);

  // Lọc dữ liệu hiển thị đa chiều
  const displayedStudents = useMemo(() => {
    return studentsList.filter(student => {
      // 1. Lọc công tắc Ẩn / Hiện thiếu nhi đã Nghỉ luôn
      const isDroppedOut = student.status === 'Nghỉ luôn' || student.status === 'Nghỉ học' || student.status === 'Đã nghỉ';
      if (!showInactiveStudents && isDroppedOut) {
        return false;
      }

      // 2. Lọc theo Lớp được chọn từ Sidebar
      const matchClass = (!selectedClassForOutlet || selectedClassForOutlet === 'all')
        ? true
        : student.classId === selectedClassForOutlet;

      if (!matchClass) return false;

      // 3. Lọc theo Vùng Ngày Đăng Ký (Từ ngày, Đến ngày)
      if (startDate || endDate) {
        const studentDate = parseStudentDate(
          student.createdAt ||
          student.registeredAt ||
          student.registrationDate ||
          student.registeredDate ||
          student.updatedAt
        );

        if (!studentDate) return false;

        if (startDate) {
          const start = new Date(startDate);
          start.setHours(0, 0, 0, 0);
          if (studentDate < start) return false;
        }

        if (endDate) {
          const end = new Date(endDate);
          end.setHours(23, 59, 59, 999);
          if (studentDate > end) return false;
        }
      }

      // 4. Lọc theo từ khóa tìm kiếm
      const term = searchTerm.toLowerCase().trim();
      if (!term) return true;

      return (
        (student.id || '').toLowerCase().includes(term) ||
        (student.fullName || '').toLowerCase().includes(term) ||
        (student.holyName || '').toLowerCase().includes(term)
      );
    });
  }, [studentsList, selectedClassForOutlet, searchTerm, startDate, endDate, showInactiveStudents]);

  const handleSelectCard = (studentId) => {
    setSelectedCards((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedCards(displayedStudents.map((student) => student.id));
    } else {
      setSelectedCards([]);
    }
  };

  const preloadImage = (src) => {
    if (!src) return Promise.resolve(null);
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.src = src;
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
    });
  };

  const downloadCard = async (studentId) => {
    const cardElement = cardRefs.current[studentId];
    if (!cardElement) return;
    try {
      const qrCodeImg = cardElement.querySelector('.qr-code-image');
      const logoImg = cardElement.querySelector('.logo');
      const backgroundImg = cardElement.querySelector('.background-img');

      const promises = [];
      if (qrCodeImg && qrCodeImg.src) promises.push(preloadImage(qrCodeImg.src));
      if (logoImg && logoImg.src) promises.push(preloadImage(logoImg.src));
      if (backgroundImg && backgroundImg.src) promises.push(preloadImage(backgroundImg.src));

      await Promise.all(promises);

      const canvas = await html2canvas(cardElement, { scale: 10, useCORS: true });
      const link = document.createElement('a');
      link.href = canvas.toDataURL('image/png');
      link.download = `card_${studentId}.png`;
      link.click();
    } catch (error) {
      console.error('Lỗi khi tải thẻ:', error);
      alert(`Có lỗi xảy ra khi tải thẻ: ${error.message}.`);
    }
  };

  const downloadSelectedCards = async () => {
    if (selectedCards.length === 0) return;
    const total = selectedCards.length;
    setShowProgress(true);
    setCurrentCount(0);
    setProgress(0);

    const zip = new JSZip();
    let processed = 0;

    for (const studentId of selectedCards) {
      const cardElement = cardRefs.current[studentId];
      if (cardElement) {
        try {
          const qrCodeImg = cardElement.querySelector('.qr-code-image');
          const logoImg = cardElement.querySelector('.logo');
          const backgroundImg = cardElement.querySelector('.background-img');

          const promises = [];
          if (qrCodeImg && qrCodeImg.src) promises.push(preloadImage(qrCodeImg.src));
          if (logoImg && logoImg.src) promises.push(preloadImage(logoImg.src));
          if (backgroundImg && backgroundImg.src) promises.push(preloadImage(backgroundImg.src));

          await Promise.all(promises);

          const canvas = await html2canvas(cardElement, { scale: 10, useCORS: true });
          const imgData = canvas.toDataURL('image/png').replace(/^data:image\/png;base64,/, '');
          zip.file(`card_${studentId}.png`, imgData, { base64: true });
        } catch (error) {
          console.error(`Lỗi khi xử lý thẻ ${studentId}:`, error);
        }
      }
      processed++;
      setCurrentCount(processed);
      setProgress((processed / total) * 100);
    }

    if (Object.keys(zip.files).length > 0) {
      zip.generateAsync({ type: 'blob' }).then((content) => {
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = 'the_thieu_nhi.zip';
        link.click();
      });
    } else {
      alert("Không có thẻ nào được xử lý để tải về.");
    }

    setShowProgress(false);
  };

  const handleBackgroundSelect = (bgType) => {
    setSelectedBackground(bgType);
    setCustomBackground(null);
    setBackgroundModalOpen(false);
  };

  const handleCustomBackgroundUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCustomBackground(reader.result);
        setSelectedBackground('custom');
        setBackgroundModalOpen(false);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="pc-container">
      <h2 className="pc-title">In Thẻ Thiếu Nhi</h2>
      
      {/* Thanh công cụ */}
      <div className="pc-toolbar">
        <button
          onClick={() => setBackgroundModalOpen(true)}
          className="pc-btn pc-btn-bg-select"
        >
          🖼️ Chọn Hình Nền
        </button>
        <button
          id="download-selected-btn"
          onClick={downloadSelectedCards}
          disabled={selectedCards.length === 0}
          className="pc-btn pc-btn-download-selected"
        >
          📥 Tải về các thẻ đã chọn ({selectedCards.length})
        </button>
      </div>

      {/* KHU VỰC BỘ LỌC NÂNG CAO */}
      <div className="pc-filter-card">
        {/* Hàng 1: Tìm kiếm */}
        <div className="pc-filter-row">
          <div className="pc-search-box">
            <input
              type="text"
              placeholder="🔎 Tìm theo ID, Tên Thánh hoặc Họ và Tên..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pc-search-input"
            />
          </div>
          <span className="pc-search-count">
            Đang hiển thị: <b>{displayedStudents.length}</b> em
          </span>
        </div>

        {/* Hàng 2: Lọc theo vùng ngày đăng ký & Công tắc ẩn/hiện nghỉ luôn */}
        <div className="pc-filter-row pc-filter-controls">
          <div className="pc-date-filter-group">
            <span className="pc-filter-label">📅 Ngày đăng ký:</span>
            <div className="pc-date-inputs">
              <label>Từ:</label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="pc-date-input"
              />
              <label>Đến:</label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="pc-date-input"
              />
              {(startDate || endDate) && (
                <button
                  type="button"
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="pc-btn-clear-date"
                  title="Xóa khoảng ngày"
                >
                  ✕ Xóa lọc ngày
                </button>
              )}
            </div>
          </div>

          <div className="pc-toggle-group">
            <label className="pc-switch-container">
              <input
                type="checkbox"
                checked={showInactiveStudents}
                onChange={(e) => setShowInactiveStudents(e.target.checked)}
                className="pc-switch-input"
              />
              <span className="pc-switch-label">
                {showInactiveStudents ? '👁️ Đang hiện cả thiếu nhi đã Nghỉ luôn' : '🙈 Đang ẩn thiếu nhi đã Nghỉ luôn'}
              </span>
            </label>
          </div>
        </div>
      </div>

      {/* Modal Chọn Hình Nền */}
      {backgroundModalOpen && (
        <div className="pc-modal-backdrop">
          <div className="pc-modal-card">
            <h3 className="pc-modal-title">Chọn Hình Nền Thẻ</h3>
            
            <div className="bg-option-grid">
              <button
                onClick={() => handleBackgroundSelect('bg-1')}
                className={`background-option bg-1 ${selectedBackground === 'bg-1' ? 'background-option-selected' : ''}`}
              >
                Mẫu 1
              </button>
              <button
                onClick={() => handleBackgroundSelect('bg-2')}
                className={`background-option bg-2 ${selectedBackground === 'bg-2' ? 'background-option-selected' : ''}`}
              >
                Mẫu 2
              </button>
              <button
                onClick={() => handleBackgroundSelect('bg-3')}
                className={`background-option bg-3 ${selectedBackground === 'bg-3' ? 'background-option-selected' : ''}`}
              >
                Mẫu 3
              </button>
              <button
                onClick={() => handleBackgroundSelect('bg-4')}
                className={`background-option bg-4 ${selectedBackground === 'bg-4' ? 'background-option-selected' : ''}`}
              >
                Mẫu 4
              </button>
              <button
                onClick={() => handleBackgroundSelect('bg-5')}
                className={`background-option bg-5 ${selectedBackground === 'bg-5' ? 'background-option-selected' : ''}`}
              >
                Mẫu 5
              </button>
            </div>

            <div className="pc-upload-section">
              <label className="pc-upload-label">
                Tải lên hình nền tùy chỉnh:
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleCustomBackgroundUpload}
                className="pc-upload-input"
              />
            </div>

            <div className="pc-modal-actions">
              <button
                onClick={() => setBackgroundModalOpen(false)}
                className="pc-btn-close"
              >
                Đóng
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tiến độ tải file ZIP */}
      {showProgress && (
        <div className="pc-progress-card">
          <h4 className="pc-progress-title">Đang nén file ZIP...</h4>
          <p className="pc-progress-text">Đã xử lý: {currentCount}/{selectedCards.length} thẻ</p>
          <div className="pc-progress-track">
            <div
              className="pc-progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      {/* Bảng danh sách thẻ */}
      <div className="pc-table-wrapper">
        <table id="card-table" className="pc-table">
          <thead>
            <tr>
              <th className="th-center" style={{ width: '40px' }}>
                <input
                  type="checkbox"
                  id="select-all-checkbox"
                  title="Chọn/Bỏ chọn tất cả"
                  onChange={handleSelectAll}
                  checked={displayedStudents.length > 0 && selectedCards.length === displayedStudents.length}
                  disabled={displayedStudents.length === 0}
                  className="card-checkbox"
                />
              </th>
              <th>Mẫu Thẻ Thiếu Nhi</th>
              <th className="th-center" style={{ width: '130px' }}>Thao Tác</th>
            </tr>
          </thead>
          <tbody id="card-table-body">
            {loading ? (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                  ⏳ Đang tải dữ liệu thiếu nhi từ Firestore...
                </td>
              </tr>
            ) : (
              displayedStudents.map((student) => (
                <Card
                  key={student.id}
                  student={student}
                  selectedBackground={selectedBackground}
                  customBackground={customBackground}
                  cardRefs={cardRefs}
                  handleSelectCard={handleSelectCard}
                  selectedCards={selectedCards}
                  downloadCard={downloadCard}
                />
              ))
            )}
            {!loading && displayedStudents.length === 0 && (
              <tr>
                <td colSpan="3" style={{ textAlign: 'center', padding: '24px', color: '#64748b' }}>
                  Không có thiếu nhi nào phù hợp với bộ lọc ngày hoặc trạng thái đang chọn.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PrintCards;